/**
 * CR-7293: Admin Setup > Quality > Cost of Quality — the monetary cost of one
 * NCR, applied to Customer AND Supplier Liability NCRs in the Supplier
 * Performance Cost of Quality columns.
 *
 * A grouped customer chooses how its group holds the rate: "Group level" (one
 * rate for every site) or "Site level" (each site sets its own). Nothing set
 * at the live level → the platform default. The API decides who may edit what
 * (group admins / Helix for the level and group rate; a site admin only the
 * sites it holds) and this component only renders those permissions.
 */
import { useCallback, useEffect, useState } from 'react';
import { qualitySettingsAPI } from '../../../lib/api';
import type { CostOfQualityLevel, CostOfQualitySettings } from '../../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { RadioGroup, RadioGroupItem } from '../../ui/radio-group';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface CostOfQualitySectionProps {
  /** Helix only: the organisation being configured (`group:<id>` / `company:<id>`). */
  organisation?: string;
  onOrganisationChange?: (key: string) => void;
}

const SOURCE_LABEL: Record<string, string> = {
  group: 'Group rate',
  site: 'Site rate',
  platform: 'Platform default',
};

function apiError(err: unknown, fallback: string): string {
  const data = (err as { response?: { data?: Record<string, unknown> } })?.response?.data;
  const first = data ? Object.values(data)[0] : undefined;
  if (typeof first === 'string') return first;
  if (Array.isArray(first) && typeof first[0] === 'string') return first[0];
  return fallback;
}

function RateInput({ id, value, disabled, placeholder, onSave }: {
  id: string; value: string | null; disabled: boolean; placeholder: string;
  onSave: (next: string | null) => void;
}) {
  // Uncontrolled + keyed on the saved value, so a save (or a failed one) re-syncs it.
  return (
    <Input
      key={`${id}-${value ?? ''}`}
      id={id}
      type="number"
      min={0}
      step={0.01}
      defaultValue={value ?? ''}
      placeholder={placeholder}
      disabled={disabled}
      className="max-w-[150px]"
      onBlur={(e) => {
        const raw = e.target.value.trim();
        if (raw === '' && value === null) return;
        if (raw !== '' && value !== null && Number(raw) === Number(value)) return;
        onSave(raw === '' ? null : raw);
      }}
    />
  );
}

export default function CostOfQualitySection({ organisation, onOrganisationChange }: CostOfQualitySectionProps) {
  const [data, setData] = useState<CostOfQualitySettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const resp = await qualitySettingsAPI.getCostOfQuality(organisation);
      setData(resp.data);
    } catch (err: unknown) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      setError(status === 403
        ? 'Only an administrator can view the Cost of Quality rate.'
        : 'Failed to load the Cost of Quality settings.');
    } finally {
      setLoading(false);
    }
  }, [organisation]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch: Parameters<typeof qualitySettingsAPI.updateCostOfQuality>[0], message: string) => {
    try {
      setSaving(true);
      const resp = await qualitySettingsAPI.updateCostOfQuality({ ...patch, organisation });
      setData(resp.data);
      toast.success(message);
    } catch (err: unknown) {
      toast.error(apiError(err, 'Failed to save the Cost of Quality'));
      await load();
    } finally {
      setSaving(false);
    }
  };

  const isGroup = data?.organisation.kind === 'group';
  const level: CostOfQualityLevel = data?.level ?? 'site';

  return (
    <Card data-testid="cost-of-quality-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold uppercase tracking-wide">Cost of Quality</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          The cost of one NCR, applied to Customer and Supplier Liability NCRs alike.
          Supplier Performance: Cost of Quality = this rate × NCRs + the reject value of those NCRs.
        </p>
        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        )}
        {!loading && error && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="h-4 w-4" /> {error}
          </div>
        )}
        {!loading && data && (
          <>
            {data.organisations.length > 1 && (
              <div className="space-y-1">
                <Label htmlFor="coq-organisation" className="text-sm text-muted-foreground">Organisation</Label>
                <select
                  id="coq-organisation"
                  value={organisation ?? `${data.organisation.kind}:${data.organisation.id}`}
                  onChange={(e) => onOrganisationChange?.(e.target.value)}
                  className="flex h-10 w-[320px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {data.organisations.map((o) => <option key={o.key} value={o.key}>{o.name}</option>)}
                </select>
              </div>
            )}
            {isGroup && (
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground">
                  How {data.organisation.name} sets the rate
                </Label>
                <RadioGroup
                  value={level}
                  onValueChange={(v) => save({ level: v as CostOfQualityLevel },
                    v === 'group' ? 'Cost of Quality set at group level' : 'Cost of Quality set at site level')}
                  disabled={!data.can_edit_group || saving}
                  className="space-y-1"
                >
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="group" id="coq-level-group" />
                    <Label htmlFor="coq-level-group" className="font-normal">Group level — one rate for all sites</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <RadioGroupItem value="site" id="coq-level-site" />
                    <Label htmlFor="coq-level-site" className="font-normal">Site level — each site sets its own</Label>
                  </div>
                </RadioGroup>
                {!data.can_edit_group && (
                  <p className="text-xs text-muted-foreground">
                    Read-only: only a group administrator can change the level or the group rate.
                  </p>
                )}
              </div>
            )}
            {isGroup && level === 'group' && (
              <div className="space-y-2">
                <Label htmlFor="coq-group-rate" className="text-sm text-muted-foreground">
                  Group rate (per NCR)
                </Label>
                <RateInput
                  id="coq-group-rate"
                  value={data.group_rate}
                  disabled={!data.can_edit_group || saving}
                  placeholder={`Default ${data.platform_default}`}
                  onSave={(next) => save({ group_rate: next }, 'Group Cost of Quality saved')}
                />
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Site</TableHead>
                  {level === 'site' && <TableHead>Site rate (per NCR)</TableHead>}
                  <TableHead>Rate applied</TableHead>
                  <TableHead>From</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.sites.map((site) => (
                  <TableRow key={site.company_id} data-testid={`coq-site-${site.company_id}`}>
                    <TableCell className="text-sm">{site.company_name}</TableCell>
                    {level === 'site' && (
                      <TableCell>
                        <RateInput
                          id={`coq-site-rate-${site.company_id}`}
                          value={site.rate}
                          disabled={!site.can_edit || saving}
                          placeholder={`Default ${data.platform_default}`}
                          onSave={(next) => save({ company_id: site.company_id, rate: next },
                            `Cost of Quality saved for ${site.company_name}`)}
                        />
                      </TableCell>
                    )}
                    <TableCell className="text-sm font-medium">{Number(site.effective_rate).toLocaleString()}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{SOURCE_LABEL[site.source] ?? site.source}</TableCell>
                  </TableRow>
                ))}
                {data.sites.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} className="text-sm text-muted-foreground">No sites you administer.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <p className="text-xs text-muted-foreground">
              Blank = platform default ({Number(data.platform_default).toLocaleString()}). Rates are read in each
              site&apos;s reporting currency.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  );
}
